// Beállítások → Webshopok (7.1): one row per Shopify shop — the token, the
// webhooks, the last event, today's events, the backfill — read-only, and one
// button for an admin: register the shop's webhooks again. The button only
// asks; the shopify loop does it on its next tick, and the row then says what
// it found.

import { useState } from "react";
import { toast } from "sonner";
import { RefreshCwIcon } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { fetchShopifyStores, reregisterShopifyWebhooks } from "@/lib/api";
import type { ShopifyStoreLine } from "@/lib/api";
import { useLoad } from "@/lib/useLoad";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable } from "@/components/emc/DataTable";
import { StatusPill } from "@/components/emc/StatusPill";
import { Callout, ErrorState } from "@/components/emc/states";
import { count, minute } from "@/components/emc/format";
import { messageOf } from "@/pages/campaigns/model";
import { BACKFILL_KINDS, attention, backfillLine, webhookLine } from "@/pages/settings/shops";

export function Webshops() {
  const [{ data, error }, reload] = useLoad(fetchShopifyStores, []);
  const [asking, setAsking] = useState<string | null>(null);

  async function reregister(line: ShopifyStoreLine) {
    setAsking(line.handle);
    try {
      await reregisterShopifyWebhooks(line.handle);
      toast.success("Kérés elküldve", {
        description: `${line.handle}: a webhookokat a következő körben regisztráljuk újra.`,
      });
      reload();
    } catch (cause) {
      toast.error("Nem sikerült", { description: messageOf(cause) });
    } finally {
      setAsking(null);
    }
  }

  const columns: ColumnDef<ShopifyStoreLine>[] = [
    {
      id: "shop",
      header: "Bolt",
      meta: { wrap: true },
      cell: ({ row }) => {
        const note = attention(row.original);
        return (
          <div className="flex flex-col gap-0.5">
            <span className="font-semibold">
              {row.original.country} · {row.original.handle}
            </span>
            {note !== null && <span className="text-label text-danger">{note}</span>}
          </div>
        );
      },
    },
    {
      id: "token",
      header: "Token",
      cell: ({ row }) => (
        <div className="flex flex-col gap-0.5">
          <StatusPill domain="shopifyToken" state={row.original.token_state} />
          {row.original.token_expires_at !== null && (
            <span className="text-label text-muted-foreground">
              lejár: {minute(row.original.token_expires_at)}
            </span>
          )}
        </div>
      ),
    },
    {
      id: "webhooks",
      header: "Webhook",
      meta: { wrap: true, mono: true },
      cell: ({ row }) => webhookLine(row.original),
    },
    {
      id: "last",
      header: "Utolsó esemény",
      meta: { mono: true, muted: true },
      cell: ({ row }) => minute(row.original.last_event_at),
    },
    {
      id: "today",
      header: "Ma",
      meta: { mono: true, align: "right" },
      cell: ({ row }) => count(row.original.events_today),
    },
    {
      id: "backfill",
      header: "Visszatöltés",
      meta: { wrap: true },
      cell: ({ row }) => (
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {BACKFILL_KINDS.map(({ kind, label }) => {
            const progress = row.original.backfill[kind];
            return (
              <li key={kind} className="flex items-center gap-2 text-label">
                <span className="w-20 text-muted-foreground">{label}</span>
                {progress !== undefined && <StatusPill domain="shopifyBackfill" state={progress.state} />}
                <span className="data-mono">{backfillLine(progress)}</span>
              </li>
            );
          })}
        </ul>
      ),
    },
    {
      id: "actions",
      header: () => <span className="sr-only">Műveletek</span>,
      meta: { align: "right" },
      cell: ({ row }) => (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!row.original.configured || asking === row.original.handle}
          title={
            row.original.reregister_requested_at !== null
              ? `Kérve: ${minute(row.original.reregister_requested_at)}`
              : undefined
          }
          onClick={() => void reregister(row.original)}
        >
          <RefreshCwIcon />
          Webhookok újraregisztrálása
        </Button>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Webshopok</CardTitle>
        <CardDescription>
          A Shopify-boltok kapcsolata: token, webhookok, beérkező események és a visszatöltés.
          {data !== null && ` API-verzió: ${data.api_version}.`}
          {data !== null &&
            (data.writeback_enabled
              ? " A leiratkozások visszaírása be van kapcsolva."
              : " A leiratkozások visszaírása ki van kapcsolva.")}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {error !== null && <ErrorState message={error} onRetry={reload} />}
        {data !== null && data.problems.length > 0 && (
          <Callout tone="warn">A boltok beállítása hibás: {data.problems.join(" · ")}</Callout>
        )}
        <DataTable
          label="Webshopok"
          columns={columns}
          data={data?.stores ?? []}
          loading={data === null && error === null}
          getRowId={(row) => row.handle}
          variant="plain"
          empty={
            <p className="px-4 py-3 text-muted-foreground">
              Nincs bolt beállítva (EMC_SHOPIFY_STORES__XX__DOMAIN).
            </p>
          }
        />
      </CardContent>
    </Card>
  );
}
